import React, { Children, Component } from 'react';
import { motion } from 'framer-motion';
import { DjayAvatar, type DjayEmotion } from '../../mascot';
import { Button } from '../ui/Button';
import { hapticManager } from '../../utils/hapticManager';
export type EmptyStatePreset = 'no-progress' | 'no-achievements' | 'no-results' | 'no-sessions' | 'completed-all' | 'custom';
type ColorScheme = 'emerald' | 'indigo' | 'slate' | 'amber' | 'blue';
interface EmptyStateProps {
  preset?: EmptyStatePreset;
  title?: string;
  message?: string;
  emotion?: DjayEmotion;
  actionLabel?: string;
  onAction?: () => void;
  colorScheme?: ColorScheme;
}
interface PresetConfig {
  title: string;
  message: string;
  emotion: DjayEmotion;
  colorScheme: ColorScheme;
  actionLabel?: string;
}
const PRESET_CONFIGS: Record<Exclude<EmptyStatePreset, 'custom'>, PresetConfig> = {
  'no-progress': {
    title: 'Klaar voor de start?',
    message: 'Je hebt nog geen oefeningen gemaakt. Laten we samen beginnen met je eerste les!',
    emotion: 'encouraging',
    colorScheme: 'emerald',
    actionLabel: 'Start eerste les'
  },
  'no-achievements': {
    title: 'Nog geen badges',
    message: 'Oefen goed en verdien je allereerste badge. Je kunt het!',
    emotion: 'excited',
    colorScheme: 'amber',
    actionLabel: 'Ga oefenen'
  },
  'no-results': {
    title: 'Niets gevonden',
    message: 'Ik heb overal gezocht, maar ik kon helaas niets vinden dat hierop lijkt.',
    emotion: 'thinking',
    colorScheme: 'indigo'
  },
  'no-sessions': {
    title: 'Kies een oefenmodus',
    message: 'Probeer een van de leuke oefenmodi om sterker te worden in rekenen of taal!',
    emotion: 'pointing',
    colorScheme: 'blue',
    actionLabel: 'Kies een modus'
  },
  'completed-all': {
    title: 'Wauw, alles is af!',
    message: 'Je hebt alle opdrachten supergoed afgerond. Tijd voor een feestje!',
    emotion: 'celebrating',
    colorScheme: 'emerald'
  }
};
const colorMap: Record<ColorScheme, string> = {
  emerald: 'bg-emerald-50 border-emerald-100',
  indigo: 'bg-indigo-50 border-indigo-100',
  slate: 'bg-slate-50 border-slate-100',
  amber: 'bg-amber-50 border-amber-100',
  blue: 'bg-blue-50 border-blue-100'
};
const particleColorMap: Record<ColorScheme, string[]> = {
  emerald: ['bg-emerald-300', 'bg-emerald-400', 'bg-emerald-200'],
  indigo: ['bg-indigo-300', 'bg-indigo-400', 'bg-indigo-200'],
  slate: ['bg-slate-300', 'bg-slate-400', 'bg-slate-200'],
  amber: ['bg-amber-300', 'bg-amber-400', 'bg-amber-200'],
  blue: ['bg-blue-300', 'bg-blue-400', 'bg-blue-200']
};
const buttonVariantMap: Record<ColorScheme, 'primary' | 'secondary' | 'success'> = {
  emerald: 'success',
  indigo: 'primary',
  slate: 'secondary',
  amber: 'primary',
  blue: 'primary'
};
// ── Animations ───────────────────────────────────────────────────────────────
const containerVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25,
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 20
    }
  }
};
const djayFloatVariants = {
  idle: {
    y: [0, -8, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
};
// ── Sub-components ───────────────────────────────────────────────────────────
function FloatingParticles({
  colorScheme
}: {
  colorScheme: ColorScheme;
}) {
  const colors = particleColorMap[colorScheme];
  // Deterministic positions so they don't jump on re-render
  const particles = [{
    id: 1,
    size: 8,
    top: '10%',
    left: '15%',
    delay: 0,
    color: colors[0]
  }, {
    id: 2,
    size: 12,
    top: '20%',
    right: '15%',
    delay: 0.5,
    color: colors[1]
  }, {
    id: 3,
    size: 6,
    bottom: '25%',
    left: '20%',
    delay: 1,
    color: colors[2]
  }, {
    id: 4,
    size: 10,
    bottom: '15%',
    right: '20%',
    delay: 1.5,
    color: colors[0]
  }];
  return <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl" data-id="element-2274">
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.color} opacity-40`} style={{
      width: p.size,
      height: p.size,
      top: p.top,
      left: p.left,
      right: p.right,
      bottom: p.bottom
    }} animate={{
      y: [0, -15, 0],
      x: [0, p.id % 2 === 0 ? 10 : -10, 0],
      opacity: [0.3, 0.6, 0.3]
    }} transition={{
      duration: 4 + p.id,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: p.delay
    }} data-id="element-2275" />)}
    </div>;
}
// ── Main Component ───────────────────────────────────────────────────────────
export function EmptyState({
  preset = 'custom',
  title: customTitle,
  message: customMessage,
  emotion: customEmotion,
  actionLabel: customActionLabel,
  onAction,
  colorScheme: customColorScheme
}: EmptyStateProps) {
  // Resolve config based on preset
  const isCustom = preset === 'custom';
  const config = isCustom ? null : PRESET_CONFIGS[preset];
  const title = isCustom ? customTitle || '' : config?.title || '';
  const message = isCustom ? customMessage || '' : config?.message || '';
  const emotion = isCustom ? customEmotion || 'happy' : config?.emotion || 'happy';
  const colorScheme = isCustom ? customColorScheme || 'slate' : config?.colorScheme || 'slate';
  const actionLabel = isCustom ? customActionLabel : config?.actionLabel || customActionLabel;
  const handleAction = () => {
    hapticManager.hapticLight();
    if (onAction) onAction();
  };
  return <div className="flex flex-col items-center justify-center py-12 px-6 w-full" data-id="element-2276">
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className={`relative rounded-3xl border-2 p-8 sm:p-10 flex flex-col items-center text-center max-w-md w-full shadow-sm ${colorMap[colorScheme]}`} data-id="element-2277">
        
        <FloatingParticles colorScheme={colorScheme} data-id="element-2278" />

        <motion.div variants={itemVariants} className="relative z-10 mb-6" data-id="element-2279">
          <motion.div variants={djayFloatVariants} animate="idle" data-id="element-2280">
            <DjayAvatar size="xl" emotion={emotion} data-id="element-2281" />
          </motion.div>
        </motion.div>

        <motion.h3 variants={itemVariants} className="relative z-10 text-xl sm:text-2xl font-bold text-slate-900 mb-3" data-id="element-2282">
          
          {title}
        </motion.h3>

        <motion.p variants={itemVariants} className="relative z-10 text-base text-slate-600 leading-relaxed max-w-sm" data-id="element-2283">
          
          {message}
        </motion.p>

        {actionLabel && onAction && <motion.div variants={itemVariants} className="relative z-10 mt-8 w-full sm:w-auto" data-id="element-2284">
          
            <Button variant={buttonVariantMap[colorScheme]} size="lg" onClick={handleAction} className="w-full sm:w-auto shadow-md hover:shadow-lg" data-id="element-2285">
            
              {actionLabel}
            </Button>
          </motion.div>}
      </motion.div>
    </div>;
}